import { useEffect, useRef, useState } from "react";

/**
 * Hook que observa um elemento e retorna `[ref, visible]`.
 * Quando `reversible` é true, esconde de novo ao sair da viewport (efeito do design original).
 */
export function useReveal<T extends HTMLElement = HTMLElement>(
  reversible = false,
  rootMargin = "0px 0px -10% 0px",
): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            if (!reversible) io.unobserve(el);
          } else if (reversible) {
            setVisible(false);
          }
        }
      },
      { rootMargin, threshold: 0.05 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reversible, rootMargin]);

  return [ref, visible];
}

/**
 * Aplica `.rv` no <html> só uma vez para habilitar opacidade inicial 0 dos `.reveal`.
 * Sem isso, em browsers sem JS o conteúdo permanece visível (fallback gracioso).
 */
export function useEnableReveal() {
  useEffect(() => {
    document.documentElement.classList.add("rv");
    return () => document.documentElement.classList.remove("rv");
  }, []);
}
